import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Drawer,
} from '@mantine/core';
import {
  IconTable,
  IconLayoutGrid,
  IconSearch,
  IconAdjustments,
} from '@tabler/icons-react';
import { Flex, Select, Stat, TextInput } from '@/components';
import { BarChartWidget } from '@/components/advanced/Widgets';

export default function CasePage() {
  const [activePage, setActivePage] = useState(1);

  // mock data for leaders for table form and cards
  const leaders = [
    {
      id: 1,
      caseName: 'Alice Johnson',
      date: '2024-06-01',
      location: 'New York',
      status: 'Active',
      leader: 'Leader 1',
      session: 'Session 1',
      counseling: 'counseling need 1',
    },
    {
      id: 2,
      caseName: 'Bob Smith',
      date: '2024-06-02',
      location: 'Los Angeles',
      status: 'Inactive',
      leader: 'Leader 2',
      session: 'Session 2',
      counseling: 'counseling need 2',
    },
    {
      id: 3,
      caseName: 'Charlie Brown',
        date: '2024-06-03',
        location: 'Chicago',
        status: 'Active',
        leader: 'Leader 3',
        session: 'Session 3',
        counseling: 'counseling need 3',
    },
    {
      id: 4,
      caseName: 'Diana Prince',
      date: '2024-06-04',
      location: 'Houston',
      status: 'Inactive',
      leader: 'Leader 4',
      session: 'Session 4',
      counseling: 'counseling need 4',
    },
    {
      id: 5,
      caseName: 'Ethan Hunt',
      date: '2024-06-05',
      location: 'Phoenix',
      status: 'Active',
      leader: 'Leader 5',
      session: 'Session 5',
      counseling: 'counseling need 5',
    },
  ];

  // search functionality for table and cards
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedLeaderName, setSelectedLeaderName] = useState<string | null>(null);
  const [selectedCounseling, setSelectedCounseling] = useState<string | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<string | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const isFilterActive = Boolean(
    selectedLeaderName ||
      selectedCounseling ||
      selectedLocation ||
      selectedStartDate ||
      selectedEndDate ||
      selectedStatus
  );

  const filteredLeaders = leaders.filter((leader) => {
    const matchesSearch =
      leader.caseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leader.date.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leader.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leader.status.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesName = selectedLeaderName ? leader.leader === selectedLeaderName : true;
    const matchesCounseling = selectedCounseling ? leader.counseling === selectedCounseling : true;
    const matchesLocation = selectedLocation ? leader.location === selectedLocation : true;
    const matchesStartDate = selectedStartDate ? leader.date >= selectedStartDate : true;
    const matchesEndDate = selectedEndDate ? leader.date <= selectedEndDate : true;
    const matchesStatus = selectedStatus ? leader.status === selectedStatus : true;

    return (
      matchesSearch &&
      matchesName &&
      matchesCounseling &&
      matchesLocation &&
      matchesStartDate &&
      matchesEndDate &&
      matchesStatus
    );
  });

  // Mock data for bar chart (cases by status)
  const casesByStatus = [
    { category: 'Active', cases: leaders.filter((l) => l.status === 'Active').length },
    { category: 'Inactive', cases: leaders.filter((l) => l.status === 'Inactive').length },
    { category: 'Other', cases: leaders.filter((l) => !['Active', 'Inactive'].includes(l.status)).length },
  ];

  const barConfig = [{ dataKey: 'cases', name: 'Cases', color: '#4c6ef5' }];

  return (
    <Container size="xl" py="xl">
      <Title order={2}>Case</Title>
      <Text mb="lg">This is the Care Support Leaders page. Replace this with your actual content.</Text>

      {/* Search and Filter */}
      <Card withBorder radius="md" padding="lg" mb="md">
        <Flex gap="sm" align="center">
          <TextInput
            placeholder="Search by case name, date, location, status..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            radius="md"
            styles={{ input: { width: '300px' } }}
            size="sm"
          />
          <Button variant="outline" leftSection={<IconAdjustments size={16} />} onClick={() => setFiltersOpen(true)}>
            Filters
          </Button>
          {isFilterActive && (
            <Button
              variant="subtle"
              onClick={() => {
                setSearchTerm('');
                setSelectedLeaderName(null);
                setSelectedCounseling(null);
                setSelectedLocation(null);
                setSelectedStartDate(null);
                setSelectedEndDate(null);
                setSelectedStatus(null);
              }}
            >
              Clear Filters
            </Button>
          )}
        </Flex>
      </Card>

      {/* Right-side filter drawer */}
      <Drawer opened={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters" position="right" size="md" padding="md">
        <Stack>
          <Select
            label="Start Date"
            placeholder="Select Start Date"
            data={Array.from(new Set(leaders.map((leader) => leader.date))).map((date) => ({ value: date, label: date }))}
            value={selectedStartDate}
            onChange={setSelectedStartDate}
          />

          <Select
            label="End Date"
            placeholder="Select End Date"
            data={Array.from(new Set(leaders.map((leader) => leader.date))).map((date) => ({ value: date, label: date }))}
            value={selectedEndDate}
            onChange={setSelectedEndDate}
          />

          <Select
            label="Status"
            placeholder="Select a status"
            data={Array.from(new Set(leaders.map((leader) => leader.status))).map((status) => ({ value: status, label: status }))}
            value={selectedStatus}
            onChange={setSelectedStatus}
          />

          <Select
            label="Counseling Need"
            placeholder="Select a counseling need"
            data={leaders.map((leader) => ({ value: leader.counseling, label: leader.counseling }))}
            value={selectedCounseling}
            onChange={setSelectedCounseling}
          />

          <Select
            label="Location"
            placeholder="Select a location"
            data={Array.from(new Set(leaders.map((leader) => leader.location))).map((location) => ({ value: location, label: location }))}
            value={selectedLocation}
            onChange={setSelectedLocation}
          />

          <Group align="right" mt="sm">
            <Button
              variant="light"
              onClick={() => {
                setSearchTerm('');
                setSelectedLeaderName(null);
                setSelectedCounseling(null);
                setSelectedLocation(null);
                setSelectedStartDate(null);
                setSelectedEndDate(null);
                setSelectedStatus(null);
                setFiltersOpen(false);
              }}
            >
              Clear
            </Button>
            <Button onClick={() => setFiltersOpen(false)}>Apply</Button>
          </Group>
        </Stack>
      </Drawer>

      {/* Table view */}
      <Card withBorder radius="md" padding="lg">
        {/* CSV EXCEL PDF PRINT buttons  */}
        <Group mb="md">
          <Button variant="outline" size="sm">
            Export CSV
          </Button>
          <Button variant="outline" size="sm">
            Export Excel
          </Button>
          <Button variant="outline" size="sm">
            Export PDF
          </Button>
          <Button variant="outline" size="sm">
            Print
          </Button>
        </Group>

        {/* 4 matrix card columns */}
        <SimpleGrid cols={{ base: 1, sm: 4 }} mb="md" spacing="md">
          <Stat label="Total Cases" value={leaders.length} icon={<IconTable size={20} color="blue" />} />
          <Stat label="Total Leaders" value={leaders.filter((leader) => leader.status === 'Active').length} icon={<IconLayoutGrid size={20} color="green" />} />
          <Stat label="Total Locations" value={leaders.filter((leader) => leader.status === 'Inactive').length} icon={<IconLayoutGrid size={20} color="red" />} />
          <Stat label="Total Needs" value={leaders.filter((leader) => leader.counseling).length} icon={<IconLayoutGrid size={20} color="yellow" />} />
        </SimpleGrid>

        {/* Bar chart */}
          <BarChartWidget title="Cases by Status" data={casesByStatus} bars={barConfig} height={220} />

          {/* Location metrix card with total number of people in that location  */}
          <Stack mt="sm">
          <Text size="lg" mt="md" mb="sm">Cases by Location</Text>
          <SimpleGrid cols={{ base: 1, sm: 4 }} mt="md" spacing="md">
            {Array.from(new Set(leaders.map((leader) => leader.location))).map((location) => (
              <Stat
                key={location}
                label={location}
                value={leaders.filter((leader) => leader.location === location).length}
                icon={<IconLayoutGrid size={20} color="blue" />}
              />
            ))}
          </SimpleGrid>
          </Stack>


      </Card>
    </Container>
  );
}
